import { Subject, take } from 'rxjs';
import {
    Component,
    inject,
    EventEmitter,
    Output,
    Input,
    OnDestroy,
    OnInit,
    ChangeDetectorRef,
    ChangeDetectionStrategy,
    AfterViewInit
} from '@angular/core';

import { NTurnstile } from './turnstile.namespace';
import { RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Logger } from '@df/core/service/logger';

let UID = 0;

@Component({
    selector: 'turnstile',
    template: '<div [attr.id]="uid"></div>',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TurnstileComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input()
    siteKey?: string;

    readonly uid = 'turnstile-container-' + UID++;

    @Output()
    resolved = new EventEmitter<string>();

    @Output()
    init = new EventEmitter<TurnstileComponent>();

    private request = inject(RequestService);

    private app = inject(App);

    private _existingApiReadyCallback: (() => void) | undefined;

    private widgetId?: string;

    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private logger: Logger
    ) {}

    ngOnInit(): void {
        this.init.emit(this);
    }

    ngAfterViewInit(): void {
        if (this.app.isPlatformBrowser) {
            if (!this.turnstile) {
                this._existingApiReadyCallback = window.onloadTurnstileCallback;

                const iframeApiAvailableSubject = new Subject<boolean>();
                window.onloadTurnstileCallback = () => {
                    if (this._existingApiReadyCallback) {
                        this._existingApiReadyCallback();
                    }
                    iframeApiAvailableSubject.next(true);
                };
                iframeApiAvailableSubject.pipe(take(1)).subscribe(() => {
                    this.render();
                });
                this.request.loadScript(NTurnstile.SCRIPT);
            } else {
                this.render();
            }
        }
    }

    ngOnDestroy(): void {
        if (this.widgetId && this.turnstile) {
            this.turnstile.remove(this.widgetId);
        }
    }

    reset(): void {
        if (this.turnstile && this.widgetId) {
            this.turnstile.reset(this.widgetId);
        } else {
            this.render();
        }
    }

    private render(): void {
        if (this.siteKey && this.turnstile) {
            try {
                this.widgetId = this.turnstile.render(this.containerSelector, {
                    sitekey: this.siteKey,
                    callback: token => {
                        this.onResponse(token);
                    },
                    'timeout-callback': () => this.onTimeout(),
                    'expired-callback': () => this.onExpired(),
                    'error-callback': error => this.onError(error),
                    'unsupported-callback': () => this.onUnsupported(),
                    appearance: NTurnstile.EAppearance.interactionOnly
                });
            } catch (e) {
                this.onError(e);
            }
        }
    }

    private onTimeout(): void {
        this.logger.error('Turnstile timeout');
        this.reset();
    }

    private onExpired(): void {
        this.logger.error('Turnstile expired');
        this.reset();
    }

    private onUnsupported(): void {
        this.logger.error('Turnstile unsupported');
    }

    private onError(error: any): void {
        this.logger.error('Turnstile error', error);
        this.onResponse();
    }

    private onResponse(token?: string) {
        this.resolved.emit(token);
        this.detectChanges();
    }

    private get containerSelector(): string {
        return `#${this.uid}`;
    }

    private get turnstile(): NTurnstile.IApi | undefined {
        if (typeof window === 'undefined') {
            return undefined;
        }
        return window.turnstile;
    }

    private detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }
}
